import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Account = { id: number; name: string }
type Stage = {
  id: number; pipeline: number; name: string; probability: string;
  sort_order: number; is_won: boolean; is_lost: boolean; color: string
}
type Pipeline = {
  id: number; name: string; is_default: boolean; is_active: boolean; stages: Stage[]
}

type Opportunity = {
  id: number
  name: string
  account: number
  account_name: string
  amount: string
  currency_code: string
  pipeline: number
  pipeline_name: string
  stage: number
  stage_name: string
  stage_color: string
  probability: string
  weighted_amount: string
  expected_close_date: string | null
  owner: number | null
  owner_username: string | null
  status: 'OPEN' | 'WON' | 'LOST'
  converted_to_quote: number | null
  converted_quote_number: string | null
}

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  name: string
  account: number | 0
  amount: string
  currency_code: string
  pipeline: number | 0
  stage: number | 0
  expected_close_date: string | null
}

const STATUS_COLOR: Record<Opportunity['status'], string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | Opportunity['status'])[] = ['ALL', 'OPEN', 'WON', 'LOST']

export default function CRMOpportunities() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const oppsQ = useQuery({
    queryKey: ['crm', 'opportunities'],
    queryFn: async () => (await api.get<Opportunity[]>('/crm/opportunities/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })
  const pipelinesQ = useQuery({
    queryKey: ['crm', 'pipelines'],
    queryFn: async () => (await api.get<Pipeline[]>('/crm/pipelines/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Opportunity['status']>('OPEN')

  function openCreate() {
    const firstPipeline = (pipelinesQ.data || []).find((p) => p.is_active)
    const firstStage = firstPipeline?.stages[0]
    setDraft({
      ...emptyDraft(),
      account: accountsQ.data?.[0]?.id ?? 0,
      pipeline: firstPipeline?.id ?? 0,
      stage: firstStage?.id ?? 0,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Opportunity>('/crm/opportunities/', {
      ...draft,
      primary_contact: null,
      probability_override: null,
      lost_reason: '',
      notes: '',
      owner: null,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] })
      toast.success('Opportunity created')
      setCreating(false)
      navigate(`/admin/crm/opportunities/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/opportunities/${id}/`),
    onSuccess: () => {
      toast.success('Opportunity deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertQuoteMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/crm/opportunities/${id}/convert_to_quote/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm'] })
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      toast.success('Converted to quote')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertEngagementMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/crm/opportunities/${id}/convert_to_engagement/`)).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['crm'] })
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
      toast.success('Converted to engagement')
      // The endpoint returns engagement_id, not id.
      if (data?.engagement_id) navigate(`/admin/consulting/engagements/${data.engagement_id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const opps = oppsQ.data || []
  const accounts = accountsQ.data || []
  const pipelines = (pipelinesQ.data || []).filter((p) => p.is_active)
  const filtered = filterStatus === 'ALL' ? opps : opps.filter((o) => o.status === filterStatus)

  const draftPipeline = pipelines.find((p) => p.id === draft.pipeline)
  const draftStages = draftPipeline?.stages || []

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Opportunities</h2>
          <p className="text-sm text-muted-foreground">
            {opps.length} total · {opps.filter((o) => o.status === 'OPEN').length} open
          </p>
        </div>
        <Button onClick={openCreate} disabled={accounts.length === 0 || pipelines.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New opportunity
        </Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {oppsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No opportunities.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Account</Th>
                <Th>Stage</Th>
                <Th className="text-right">Prob.</Th>
                <Th className="text-right">Weighted</Th>
                <Th>Close date</Th>
                <Th>Status</Th>
                <Th>Owner</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/crm/opportunities/${o.id}`)}
                >
                  <Td className="font-medium">{o.name}</Td>
                  <Td>{o.account_name}</Td>
                  <Td>
                    <span className="text-xs px-2 py-0.5 rounded font-semibold"
                      style={{ backgroundColor: (o.stage_color || '#94a3b8') + '33', color: o.stage_color || '#475569' }}>
                      {o.stage_name}
                    </span>
                  </Td>
                  <Td className="text-right font-mono text-xs">{Number(o.probability).toFixed(0)}%</Td>
                  <Td className="text-right font-mono text-xs">{Number(o.weighted_amount).toFixed(2)}</Td>
                  <Td className="text-xs">{o.expected_close_date || <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[o.status]}`}>
                      {o.status}
                    </span>
                    {o.converted_quote_number && (
                      <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                        <ArrowRight className="h-3 w-3" /> {o.converted_quote_number}
                      </div>
                    )}
                  </Td>
                  <Td className="text-xs">{o.owner_username || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/crm/opportunities/${o.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {!o.converted_to_quote && (
                          <DropdownMenuItem
                            onClick={() => convertQuoteMut.mutate(o.id)}
                            disabled={convertQuoteMut.isPending}
                            className="text-green-700"
                          >
                            Convert to quote
                          </DropdownMenuItem>
                        )}
                        {o.status === 'WON' && (
                          <DropdownMenuItem
                            onClick={() => convertEngagementMut.mutate(o.id)}
                            disabled={convertEngagementMut.isPending}
                            className="text-purple-700"
                          >
                            Convert to engagement
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!o.converted_to_quote}
                          onClick={() => !o.converted_to_quote && confirm(
                            `opportunity ${o.name}`,
                            () => deleteMut.mutate(o.id),
                          )}
                          className={!o.converted_to_quote ? 'text-destructive' : ''}
                        >
                          {o.converted_to_quote ? `Delete (converted)` : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Rest filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New opportunity"
        description="Create with the essentials. Probability, primary contact, lost reason, and notes are filled in on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="op-name">Name *</FieldLabel>
            <Input id="op-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })}
              placeholder="Q3 platform expansion" />
          </Field>

          <Field>
            <FieldLabel htmlFor="op-account">Account *</FieldLabel>
            <SearchableSelect
              id="op-account"
              value={draft.account ? String(draft.account) : undefined}
              onChange={(v) => setDraft({ ...draft, account: Number(v) })}
              placeholder="— select —"
              options={accounts.map((a) => ({ value: String(a.id), label: a.name }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="op-amount">Amount</FieldLabel>
              <Input id="op-amount" type="number" step="0.01" value={draft.amount}
                onChange={(ev) => setDraft({ ...draft, amount: ev.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="op-currency">Currency</FieldLabel>
              <Input id="op-currency" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="op-pipeline">Pipeline *</FieldLabel>
              <SearchableSelect
                id="op-pipeline"
                value={draft.pipeline ? String(draft.pipeline) : undefined}
                onChange={(v) => {
                  const pid = Number(v)
                  const firstStage = pipelines.find((p) => p.id === pid)?.stages[0]?.id ?? 0
                  setDraft({ ...draft, pipeline: pid, stage: firstStage })
                }}
                options={pipelines.map((p) => ({ value: String(p.id), label: p.name }))}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="op-stage">Stage *</FieldLabel>
              <SearchableSelect
                id="op-stage"
                value={draft.stage ? String(draft.stage) : undefined}
                onChange={(v) => setDraft({ ...draft, stage: Number(v) })}
                options={draftStages.map((s) => ({
                  value: String(s.id),
                  label: `${s.name} (${Number(s.probability).toFixed(0)}%)`,
                }))}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="op-close">Expected close date</FieldLabel>
            <DatePicker id="op-close" value={draft.expected_close_date || ''}
              onChange={(v) => setDraft({ ...draft, expected_close_date: v || null })} />
            <FieldDescription>Used for the weighted-amount forecast.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    name: '', account: 0, amount: '0', currency_code: 'EUR',
    pipeline: 0, stage: 0, expected_close_date: null,
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
